"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/lib/logger";

/**
 * Web Speech API の最小限の型定義。
 *
 * `SpeechRecognition` は標準 DOM 型に含まれず、Chrome では `webkitSpeechRecognition`
 * の prefix でのみ提供されるため、依存する部分だけを自前で宣言する。global 宣言を
 * 増やすとプロジェクト全体の型と衝突しうるので、ここではモジュール内ローカル型に留める。
 */
type SpeechRecognitionAlternativeLike = {
  readonly transcript: string;
};

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean;
  readonly length: number;
  [index: number]: SpeechRecognitionAlternativeLike | undefined;
};

type SpeechRecognitionResultListLike = {
  readonly length: number;
  [index: number]: SpeechRecognitionResultLike | undefined;
};

type SpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultListLike;
};

type SpeechRecognitionErrorEventLike = {
  readonly error: string;
};

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

type SpeechRecognitionSession = {
  recognition: SpeechRecognitionLike;
  acceptsResults: boolean;
};

/**
 * 呼び出し側がユーザー向け文言を選べるよう、失敗理由を正規化した種別。
 * `not-allowed` はマイク権限拒否、`audio-capture` はデバイス未検出に対応する。
 */
export type SpeechInputErrorReason =
  | "not-allowed"
  | "no-speech"
  | "audio-capture"
  | "aborted"
  | "network"
  | "unknown";

type SpeechInputStatus = "idle" | "starting" | "listening" | "stopping";

export type UseSpeechInputOptions = {
  /** 認識エンジンへ渡す BCP-47 言語タグ（例: "ja-JP", "en-US"）。 */
  lang: string;
  /** 発話が確定するたびに、確定済みテキスト片を渡して呼ばれる。 */
  onResult: (transcript: string) => void;
  /** 認識失敗時に呼ばれる。権限拒否は `not-allowed` に正規化される。 */
  onError?: (reason: SpeechInputErrorReason) => void;
};

export type UseSpeechInputResult = {
  /** 現在のブラウザが Web Speech API に対応しているか。 */
  isSupported: boolean;
  /** 録音（音声認識）が進行中か。 */
  isListening: boolean;
  /** Web Speech API の開始・録音・停止を含む現在の状態。 */
  status: SpeechInputStatus;
  /**
   * まだ確定していない認識中テキスト。録音中はリアルタイムに更新され、確定・停止・
   * エラー時に空文字へ戻る。呼び出し側が「仮入力」表示に使う。
   */
  interimTranscript: string;
  /** 録音を開始する。未対応・録音中の場合は何もしない。 */
  start: () => void;
  /** 録音を停止して結果を確定させる。 */
  stop: () => void;
  /** 録音を中断し、以後そのセッションから届く認識結果を破棄する。 */
  abort: () => void;
  /** 録音状態をトグルする。 */
  toggle: () => void;
};

/** ブラウザ固有のエラー文字列を、UI 側で扱いやすい種別へ正規化する。 */
function normalizeSpeechError(error: string): SpeechInputErrorReason {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return "not-allowed";
    case "no-speech":
      return "no-speech";
    case "audio-capture":
      return "audio-capture";
    case "aborted":
      return "aborted";
    case "network":
      return "network";
    default:
      return "unknown";
  }
}

function getSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof window === "undefined") {
    return null;
  }
  const candidate =
    (
      window as unknown as {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
      }
    ).SpeechRecognition ??
    (
      window as unknown as {
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
      }
    ).webkitSpeechRecognition;
  return candidate ?? null;
}

/**
 * AI Elements の `SpeechInput` と同じ Web Speech API 設定を使い、音声入力を制御する。
 *
 * Amethyst では送信後に古い認識結果が draft へ戻らないことが必要なため、assistant-ui の
 * dictation adapter と同様に開始ごとに session を作る。`stop()` は最後の確定結果を受け取り、
 * `abort()` は結果受付を先に閉じて session を破棄する。
 */
export function useSpeechInput({
  lang,
  onResult,
  onError,
}: UseSpeechInputOptions): UseSpeechInputResult {
  const [isSupported, setIsSupported] = useState(false);
  const [status, setStatus] = useState<SpeechInputStatus>("idle");
  const [interimTranscript, setInterimTranscript] = useState("");
  const constructorRef = useRef<SpeechRecognitionConstructor | null>(null);
  const sessionRef = useRef<SpeechRecognitionSession | null>(null);
  const statusRef = useRef<SpeechInputStatus>("idle");
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const langRef = useRef(lang);

  onResultRef.current = onResult;
  onErrorRef.current = onError;
  langRef.current = lang;

  const updateStatus = useCallback((nextStatus: SpeechInputStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  }, []);

  useEffect(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionConstructor();
    constructorRef.current = SpeechRecognitionCtor;
    setIsSupported(SpeechRecognitionCtor !== null);

    return () => {
      constructorRef.current = null;
      const session = sessionRef.current;
      sessionRef.current = null;
      statusRef.current = "idle";
      if (!session) {
        return;
      }

      session.acceptsResults = false;
      const { recognition } = session;
      recognition.onstart = null;
      recognition.onend = null;
      recognition.onresult = null;
      recognition.onerror = null;
      try {
        recognition.abort();
      } catch (error) {
        logger.error(error, {
          feature: "speech-input",
          operation: "unmount-abort",
        });
      }
    };
  }, []);

  const start = useCallback(() => {
    const SpeechRecognitionCtor = constructorRef.current;
    if (!SpeechRecognitionCtor || statusRef.current !== "idle") {
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    const session: SpeechRecognitionSession = {
      recognition,
      acceptsResults: true,
    };
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = langRef.current;

    recognition.onstart = () => {
      if (sessionRef.current !== session || !session.acceptsResults) {
        return;
      }
      updateStatus("listening");
    };
    recognition.onend = () => {
      if (sessionRef.current !== session) {
        return;
      }
      session.acceptsResults = false;
      sessionRef.current = null;
      updateStatus("idle");
      setInterimTranscript("");
    };
    recognition.onresult = (event) => {
      if (sessionRef.current !== session || !session.acceptsResults) {
        return;
      }

      let finalTranscript = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result?.[0]?.transcript ?? "";
        if (result?.isFinal) {
          finalTranscript += transcript;
        } else {
          interimText += transcript;
        }
      }
      if (finalTranscript) {
        onResultRef.current(finalTranscript);
      }
      setInterimTranscript(interimText);
    };
    recognition.onerror = (event) => {
      if (sessionRef.current !== session || !session.acceptsResults) {
        return;
      }
      session.acceptsResults = false;
      sessionRef.current = null;
      updateStatus("idle");
      setInterimTranscript("");
      onErrorRef.current?.(normalizeSpeechError(event.error));
    };

    sessionRef.current = session;
    setInterimTranscript("");
    // `onstart` より先に同期 ref を更新し、同一 tick の二重 start を防ぐ。
    updateStatus("starting");
    try {
      recognition.start();
    } catch (error) {
      session.acceptsResults = false;
      if (sessionRef.current === session) {
        sessionRef.current = null;
      }
      updateStatus("idle");
      logger.error(error, {
        feature: "speech-input",
        operation: "start",
      });
      onErrorRef.current?.("unknown");
    }
  }, [updateStatus]);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    if (!session || statusRef.current !== "listening") {
      return;
    }
    updateStatus("stopping");
    try {
      session.recognition.stop();
    } catch (error) {
      session.acceptsResults = false;
      if (sessionRef.current === session) {
        sessionRef.current = null;
      }
      updateStatus("idle");
      setInterimTranscript("");
      logger.error(error, {
        feature: "speech-input",
        operation: "stop",
      });
      onErrorRef.current?.("unknown");
    }
  }, [updateStatus]);

  const abort = useCallback(() => {
    const session = sessionRef.current;
    if (!session || statusRef.current === "idle") {
      return;
    }
    // abort 後に遅れて result が届いても draft を再変更しないよう、API 呼び出し前に閉じる。
    session.acceptsResults = false;
    updateStatus("stopping");
    setInterimTranscript("");
    try {
      session.recognition.abort();
    } catch (error) {
      if (sessionRef.current === session) {
        sessionRef.current = null;
      }
      updateStatus("idle");
      logger.error(error, {
        feature: "speech-input",
        operation: "abort",
      });
      onErrorRef.current?.("unknown");
    }
  }, [updateStatus]);

  const toggle = useCallback(() => {
    switch (statusRef.current) {
      case "idle":
        start();
        break;
      case "starting":
        abort();
        break;
      case "listening":
        stop();
        break;
      case "stopping":
        break;
    }
  }, [abort, start, stop]);

  return {
    isSupported,
    isListening: status !== "idle",
    status,
    interimTranscript,
    start,
    stop,
    abort,
    toggle,
  };
}
